import { useTranslation } from 'react-i18next'
import { Box, Card, CardActionArea, CardMedia, Chip, CircularProgress, Grid, Stack, Typography } from '@mui/material'
import BluetoothIcon from '@mui/icons-material/Bluetooth'
import UsbIcon from '@mui/icons-material/Usb'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'

import { ConnectorDeviceProfile, DEVICES, DEVICE_IMG } from '@/constants/medconnect-devices-types'

import { getDeviceSettingsSpec, DeviceConnectionKind } from '../../model/device-settings-spec'

export type ChooseDeviceStepProps = {
  profiles: ConnectorDeviceProfile[]
  isLoading: boolean
  selected: DEVICES | null
  onSelect: (device: DEVICES) => void
}

const CONNECTION_ICONS: Record<DeviceConnectionKind, JSX.Element> = {
  bluetooth: <BluetoothIcon fontSize="small" />,
  serial: <UsbIcon fontSize="small" />,
  folder: <FolderOpenIcon fontSize="small" />,
  program: <FolderOpenIcon fontSize="small" />,
}

/** Second step: pick the device from a catalogue of cards, not a dropdown of internal names. */
export default function ChooseDeviceStep({ profiles, isLoading, selected, onSelect }: ChooseDeviceStepProps) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={28} />
      </Stack>
    )
  }

  const knownProfiles = profiles.filter(isKnownDevice)

  return (
    <Stack spacing={2}>
      <Typography color="text.secondary">{t('page.configuration.MedconnectDevice.wizard.device.hint')}</Typography>
      <Grid container spacing={2}>
        {knownProfiles.map((profile) => (
          <Grid item xs={12} sm={6} key={profile.name}>
            <DeviceProfileCard profile={profile} isSelected={selected === profile.name} onSelect={() => onSelect(profile.name)} />
          </Grid>
        ))}
      </Grid>
    </Stack>
  )
}

function isKnownDevice(profile: ConnectorDeviceProfile): boolean {
  return Object.values(DEVICES).includes(profile.name)
}

type DeviceProfileCardProps = {
  profile: ConnectorDeviceProfile
  isSelected: boolean
  onSelect: () => void
}

function DeviceProfileCard({ profile, isSelected, onSelect }: DeviceProfileCardProps) {
  const { t } = useTranslation()
  const spec = getDeviceSettingsSpec(profile.name)
  const image = DEVICE_IMG[profile.name]
  const label = t(`component.ui.deviceCard.deviceLabels.${profile.name}`, { defaultValue: profile.displayName })
  const hint = t(`page.configuration.MedconnectDevice.deviceHints.${profile.name}`, { defaultValue: '' })

  return (
    <Card variant="outlined" sx={{ height: '100%', borderColor: isSelected ? 'primary.main' : undefined, borderWidth: isSelected ? 2 : 1 }}>
      <CardActionArea onClick={onSelect} sx={{ height: '100%', p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {image ? <CardMedia component="img" image={image} alt={label} sx={{ maxWidth: 64, maxHeight: 64, objectFit: 'contain' }} /> : <MonitorHeartIcon color="primary" sx={{ fontSize: 40 }} />}
          </Box>
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600}>{label}</Typography>
            <Typography variant="body2" color="text.secondary">{profile.displayName}</Typography>
            {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
            <Chip size="small" variant="outlined" icon={CONNECTION_ICONS[spec.connection]} label={t(`page.configuration.MedconnectDevice.wizard.connection.kind.${spec.connection}`)} sx={{ alignSelf: 'flex-start', mt: 0.5 }} />
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  )
}
